import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  getFullProfile,
  getPublicProfile,
  updateProfileSettings,
  addMedia,
  updateMedia,
  uploadFile,
} from '../services/api';
import DesignPanel from '../components/Showcase/DesignPanel';
import { InlineTextPopover, InlineColorPopover } from '../components/Showcase/InlinePopovers';
import { PhotoPicker, GalleryCropOverlay } from '../components/Showcase/PhotoPicker';
import TemplateRenderer from '../components/Showcase/TemplateRenderer';
import LegacyTemplate from '../components/Showcase/LegacyTemplate';
import ShowcaseQR from '../components/ShowcaseQR';
import '../components/ShowcaseQR.css';
import '../styles/ProfileThemes.css';
import { colors } from '../styles/theme';
import './Showcase.css';

function Showcase({ slug: slugProp }) {
  const { userId, slug: slugParam } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const slug = slugProp || slugParam;

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [basicUser, setBasicUser] = useState(null);
  const [applauseCount, setApplauseCount] = useState(0);
  const [hasApplauded, setHasApplauded] = useState(false);
  const [activeCreditsTab, setActiveCreditsTab] = useState(null);
  const [activeSection, setActiveSection] = useState('about');
  const [designPanelOpen, setDesignPanelOpen] = useState(false);
  const [editableSettings, setEditableSettings] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingDesign, setSavingDesign] = useState(false);
  const [inlineEditTarget, setInlineEditTarget] = useState(null);
  const [inlineEditRect, setInlineEditRect] = useState(null);
  const inlinePopoverRef = useRef(null);
  const [colorPickerRect, setColorPickerRect] = useState(null);
  const colorPickerRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastChangedFieldRef = useRef(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [professions, setProfessions] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [photoPickerSlot, setPhotoPickerSlot] = useState(null);
  const [photoPickerPosition, setPhotoPickerPosition] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [repositioningPhoto, setRepositioningPhoto] = useState(null);
  const photoPickerRef = useRef(null);
  const photoFileInputRef = useRef(null);
  const [galleryEditMode, setGalleryEditMode] = useState(false);
  const [galleryCropPhoto, setGalleryCropPhoto] = useState(null);
  const [galleryCropRect, setGalleryCropRect] = useState(null);
  const [showQR, setShowQR] = useState(false);

  // Determine which user to load
  const [resolvedUserId, setResolvedUserId] = useState(userId || null);
  const targetUserId = resolvedUserId || currentUser?.id;
  const isOwnProfile = currentUser && targetUserId && currentUser.id === parseInt(targetUserId);
  // When viewing via custom slug URL, the page is the published live version (no editing).
  // Editing is only allowed via the editor route (/showcase without a slug).
  const isViewingViaSlug = !!slug;
  const canEdit = isOwnProfile && !isViewingViaSlug;
  const isPublicView = !currentUser;

  useEffect(() => {
    if (slug) {
      loadProfileBySlug();
    } else if (targetUserId) {
      loadProfileData(targetUserId);
    }
  }, [slug, targetUserId]);

  const refreshProfileData = async () => {
    if (!targetUserId) return;
    try {
      const fullProfileRes = await getFullProfile(targetUserId);
      const fullData = fullProfileRes.data;
      setProfileData(fullData);
      setBasicUser(fullData.user || null);
      // Load photos from full profile media
      if (fullData.media) {
        const mediaData = Array.isArray(fullData.media) ? fullData.media : [];
        setAllPhotos(mediaData.filter((m) => m.media_type === 'image'));
      }
    } catch (e) {
      console.error('Error refreshing profile:', e);
    }
  };

  const loadProfileBySlug = async () => {
    setLoading(true);
    try {
      const res = await getPublicProfile(slug);
      const fullData = res.data;
      setProfileData(fullData);
      setBasicUser(fullData.user || null);
      setEditableSettings(fullData.settings || {});
      if (fullData.user?.id) setResolvedUserId(fullData.user.id);
      if (fullData.professions)
        setProfessions(Array.isArray(fullData.professions) ? fullData.professions : []);
      if (fullData.media) {
        const mediaData = Array.isArray(fullData.media) ? fullData.media : [];
        setAllPhotos(mediaData.filter((m) => m.media_type === 'image'));
      }
      if (fullData.credits) {
        const categories = Object.keys(fullData.credits);
        if (categories.length > 0) setActiveCreditsTab(categories[0]);
      }
    } catch (error) {
      console.error('Error loading profile by slug:', error);
    }
    setLoading(false);
  };

  const loadProfileData = async (uid) => {
    setLoading(true);
    try {
      const fullProfileRes = await getFullProfile(uid);
      const fullData = fullProfileRes.data;

      setProfileData(fullData);
      setBasicUser(fullData.user || null);
      setEditableSettings(fullData.settings || {});
      if (fullData.professions)
        setProfessions(Array.isArray(fullData.professions) ? fullData.professions : []);

      // Photos come from the full profile media array
      if (fullData.media) {
        const mediaData = Array.isArray(fullData.media) ? fullData.media : [];
        setAllPhotos(mediaData.filter((m) => m.media_type === 'image'));
      }

      if (fullData.credits) {
        const categories = Object.keys(fullData.credits);
        if (categories.length > 0) setActiveCreditsTab(categories[0]);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
    setLoading(false);
  };

  const handleApplause = () => {
    if (!hasApplauded) {
      setApplauseCount((prev) => prev + 1);
      setHasApplauded(true);
    }
  };

  // Derived undo/redo state
  const canUndo = historyVersion >= 0 && undoStackRef.current.length > 0;
  const canRedo = historyVersion >= 0 && redoStackRef.current.length > 0;

  const handleDesignChange = (field, value) => {
    if (field !== lastChangedFieldRef.current) {
      undoStackRef.current = [...undoStackRef.current.slice(-29), { ...editableSettings }];
      redoStackRef.current = [];
    }
    lastChangedFieldRef.current = field;
    setEditableSettings((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    setHistoryVersion((v) => v + 1);
  };

  const handleSaveDesign = async () => {
    setSavingDesign(true);
    try {
      await updateProfileSettings(editableSettings);
      setProfileData((prev) => ({ ...prev, settings: { ...prev.settings, ...editableSettings } }));
      setHasUnsavedChanges(false);
      undoStackRef.current = [];
      redoStackRef.current = [];
      lastChangedFieldRef.current = null;
      setHistoryVersion((v) => v + 1);
    } catch (error) {
      console.error('Error saving design settings:', error);
    }
    setSavingDesign(false);
  };

  const handleDiscardChanges = () => {
    setEditableSettings(profileData?.settings || {});
    setHasUnsavedChanges(false);
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastChangedFieldRef.current = null;
    setHistoryVersion((v) => v + 1);
  };

  const handleUndo = () => {
    if (undoStackRef.current.length === 0) return;
    const stack = [...undoStackRef.current];
    const prev = stack.pop();
    undoStackRef.current = stack;
    redoStackRef.current = [...redoStackRef.current, editableSettings];
    lastChangedFieldRef.current = null;
    setEditableSettings(prev);
    const saved = JSON.stringify(profileData?.settings || {});
    setHasUnsavedChanges(JSON.stringify(prev) !== saved);
    setHistoryVersion((v) => v + 1);
  };

  const handleRedo = () => {
    if (redoStackRef.current.length === 0) return;
    const stack = [...redoStackRef.current];
    const next = stack.pop();
    redoStackRef.current = stack;
    undoStackRef.current = [...undoStackRef.current, editableSettings];
    lastChangedFieldRef.current = null;
    setEditableSettings(next);
    const saved = JSON.stringify(profileData?.settings || {});
    setHasUnsavedChanges(JSON.stringify(next) !== saved);
    setHistoryVersion((v) => v + 1);
  };

  const handleColorClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setInlineEditTarget(null);
    setColorPickerRect({
      top: rect.bottom + window.scrollY + 8,
      left: Math.min(rect.left + rect.width / 2, window.innerWidth - 160),
    });
  }, []);

  const handleToggleSection = (sectionId) => {
    const current = editableSettings?.hidden_sections || [];
    const updated = current.includes(sectionId)
      ? current.filter((s) => s !== sectionId)
      : [...current, sectionId];
    handleDesignChange('hidden_sections', updated);
  };

  const handleTextClick = useCallback((type, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setColorPickerRect(null);
    setInlineEditTarget(type);
    setInlineEditRect({
      top: rect.bottom + window.scrollY + 8,
      left: Math.min(rect.left + rect.width / 2, window.innerWidth - 170),
    });
  }, []);

  // Close inline popovers on click outside
  useEffect(() => {
    if (!inlineEditTarget && !colorPickerRect) return;
    const handleClickOutside = (e) => {
      if (
        inlineEditTarget &&
        inlinePopoverRef.current &&
        !inlinePopoverRef.current.contains(e.target) &&
        !e.target.closest('.showcase-editable')
      ) {
        setInlineEditTarget(null);
      }
      if (
        colorPickerRect &&
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target) &&
        !e.target.closest('.showcase-editable-color')
      ) {
        setColorPickerRect(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inlineEditTarget, colorPickerRect]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    if (!canEdit) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (redoStackRef.current.length > 0) handleRedo();
        } else {
          if (undoStackRef.current.length > 0) handleUndo();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Photo management handlers
  const handlePhotoClick = useCallback((slotName, e) => {
    if (e) {
      const rect = e.currentTarget.getBoundingClientRect();
      setPhotoPickerPosition({
        top: Math.min(rect.bottom + 8, window.innerHeight - 500),
        left: Math.min(rect.left, window.innerWidth - 360),
      });
    } else {
      setPhotoPickerPosition({ top: 200, left: window.innerWidth / 2 - 170 });
    }
    setInlineEditTarget(null);
    setColorPickerRect(null);
    setPhotoPickerSlot(slotName);
  }, []);

  const handleGalleryEdit = useCallback(() => {
    setGalleryEditMode((prev) => !prev);
    setInlineEditTarget(null);
    setColorPickerRect(null);
    setPhotoPickerSlot(null);
  }, []);

  const handleGalleryReorder = async (updates) => {
    try {
      await Promise.all(updates.map((u) => updateMedia(u.id, { sort_order: u.sort_order })));
      await refreshProfileData();
    } catch (e) {
      console.error('Error reordering gallery:', e);
    }
  };

  const handleGalleryPhotoCrop = useCallback((photo, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setGalleryCropPhoto(photo);
    setGalleryCropRect({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
      aspectRatio: rect.width / rect.height,
    });
    setInlineEditTarget(null);
    setColorPickerRect(null);
    setPhotoPickerSlot(null);
  }, []);

  const handleGalleryCropSave = async (x, y, zoom) => {
    if (!galleryCropPhoto) return;
    try {
      await updateMedia(galleryCropPhoto.id, { position_x: x, position_y: y, zoom });
      setGalleryCropPhoto(null);
      setGalleryCropRect(null);
      await refreshProfileData();
    } catch (error) {
      console.error('Error saving gallery crop:', error);
    }
  };

  const handleGalleryCropCancel = useCallback(() => {
    setGalleryCropPhoto(null);
    setGalleryCropRect(null);
  }, []);

  const handlePickerPhotoUpload = async (files) => {
    if (!files || files.length === 0 || !photoPickerSlot) return;
    setUploadingPhoto(true);
    try {
      for (const file of Array.from(files).slice(0, 5)) {
        const uploadRes = await uploadFile(file);
        const url = uploadRes.data.url;
        const mediaRes = await addMedia({ media_type: 'image', url, slot: photoPickerSlot });
        const newPhoto = { id: mediaRes.data.id, media_type: 'image', url, slot: photoPickerSlot };
        setAllPhotos((prev) => [...prev, newPhoto]);
      }
      await refreshProfileData();
    } catch (error) {
      console.error('Error uploading photo:', error);
    }
    setUploadingPhoto(false);
  };

  const handlePickerAssignPhoto = async (photoId) => {
    if (!photoPickerSlot) return;
    try {
      await updateMedia(photoId, { slot: photoPickerSlot });
      setAllPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, slot: photoPickerSlot } : p))
      );
      await refreshProfileData();
    } catch (error) {
      console.error('Error assigning photo:', error);
    }
  };

  const handlePickerUnassignPhoto = async (photoId) => {
    try {
      await updateMedia(photoId, { slot: null });
      setAllPhotos((prev) => prev.map((p) => (p.id === photoId ? { ...p, slot: null } : p)));
      await refreshProfileData();
    } catch (error) {
      console.error('Error unassigning photo:', error);
    }
  };

  const handleRepositionSave = async (x, y, zoom) => {
    if (!repositioningPhoto) return;
    try {
      await updateMedia(repositioningPhoto.id, { position_x: x, position_y: y, zoom });
      setAllPhotos((prev) =>
        prev.map((p) =>
          p.id === repositioningPhoto.id ? { ...p, position_x: x, position_y: y, zoom } : p
        )
      );
      setRepositioningPhoto(null);
      await refreshProfileData();
    } catch (error) {
      console.error('Error saving position:', error);
    }
  };

  // Close photo picker on click outside
  useEffect(() => {
    if (!photoPickerSlot) return;
    const handleClickOutside = (e) => {
      if (
        photoPickerRef.current &&
        !photoPickerRef.current.contains(e.target) &&
        !e.target.closest('.photo-edit-overlay')
      ) {
        setPhotoPickerSlot(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [photoPickerSlot]);

  // Loading state
  if (loading) {
    return (
      <div className="profile-page" data-profile-theme="classic-spotlight">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            color: 'var(--profile-text)',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '50px',
                height: '50px',
                border: '3px solid var(--profile-border)',
                borderTopColor: 'var(--profile-primary)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 20px',
              }}
            ></div>
            <p>Loading showcase...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (!profileData || !basicUser) {
    return (
      <div className="profile-page" data-profile-theme="classic-spotlight">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            color: 'var(--profile-text)',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <h2>Profile not found</h2>
            <p>This showcase profile doesn't exist or hasn't been set up yet.</p>
            {isOwnProfile && (
              <button
                onClick={() => navigate('/profile-settings')}
                style={{
                  marginTop: '20px',
                  padding: '12px 24px',
                  background: 'var(--profile-primary)',
                  color: 'var(--profile-bg)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                Set Up Your Showcase
              </button>
            )}
            {isPublicView && (
              <button
                onClick={() => navigate('/login')}
                style={{
                  marginTop: '20px',
                  padding: '12px 24px',
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                Join Showcase
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Build live profile data with editable settings for real-time preview
  const liveProfileData = editableSettings
    ? {
        ...profileData,
        settings: { ...profileData.settings, ...editableSettings },
      }
    : profileData;

  const { settings } = liveProfileData;

  // Get theme — fallback removed templates to bold
  const rawTheme = settings?.theme || 'bold';
  const theme = rawTheme === 'classic-spotlight' || rawTheme === 'minimal' ? 'bold' : rawTheme;

  const hiddenSections = editableSettings?.hidden_sections || [];

  // Shared overlay components
  const designPanelElement = canEdit && editableSettings && (
    <DesignPanel
      designPanelOpen={designPanelOpen}
      setDesignPanelOpen={setDesignPanelOpen}
      editableSettings={editableSettings}
      hasUnsavedChanges={hasUnsavedChanges}
      savingDesign={savingDesign}
      canUndo={canUndo}
      canRedo={canRedo}
      onDesignChange={handleDesignChange}
      onSaveDesign={handleSaveDesign}
      onDiscardChanges={handleDiscardChanges}
      onUndo={handleUndo}
      onRedo={handleRedo}
    />
  );

  const inlinePopoverElement = canEdit && (
    <InlineTextPopover
      inlinePopoverRef={inlinePopoverRef}
      inlineEditTarget={inlineEditTarget}
      inlineEditRect={inlineEditRect}
      editableSettings={editableSettings}
      onDesignChange={handleDesignChange}
    />
  );

  const colorPopoverElement = canEdit && (
    <InlineColorPopover
      colorPickerRef={colorPickerRef}
      colorPickerRect={colorPickerRect}
      editableSettings={editableSettings}
      onDesignChange={handleDesignChange}
    />
  );

  const photoPickerElement = canEdit && (
    <PhotoPicker
      photoPickerRef={photoPickerRef}
      photoFileInputRef={photoFileInputRef}
      photoPickerSlot={photoPickerSlot}
      photoPickerPosition={photoPickerPosition}
      allPhotos={allPhotos}
      uploadingPhoto={uploadingPhoto}
      repositioningPhoto={repositioningPhoto}
      onClose={() => setPhotoPickerSlot(null)}
      onPickerPhotoUpload={handlePickerPhotoUpload}
      onPickerAssignPhoto={handlePickerAssignPhoto}
      onPickerUnassignPhoto={handlePickerUnassignPhoto}
      onSetRepositioningPhoto={setRepositioningPhoto}
      onRepositionSave={handleRepositionSave}
    />
  );

  const galleryCropElement = (
    <GalleryCropOverlay
      galleryCropPhoto={galleryCropPhoto}
      galleryCropRect={galleryCropRect}
      onSave={handleGalleryCropSave}
      onCancel={handleGalleryCropCancel}
    />
  );

  // QR button removed from showcase live page — moved to ProfileSettings sidebar.
  const qrButton = null;

  const qrModal = showQR && basicUser?.slug && (
    <ShowcaseQR
      slug={basicUser.slug || slug}
      displayName={basicUser?.full_name || basicUser?.username}
      onClose={() => setShowQR(false)}
    />
  );

  // In-app banner for logged-in users viewing via slug URL
  const inAppBanner =
    currentUser && slug ? (
      /* REVIEW: theme tokens */
      <div
        className="showcase-slug-banner"
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: '#2e2e2e',
          color: '#fff',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          fontFamily: "'Rethink Sans', sans-serif",
          fontSize: '14px',
          borderBottom: `3px solid ${colors.accentPurple}`,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {isOwnProfile ? (
          <span>
            This is your <strong>live page</strong> — view-only. To edit, open the editor.
          </span>
        ) : (
          <span>
            You're viewing <strong>{basicUser?.full_name || basicUser?.username}'s</strong> Showcase
            page
          </span>
        )}
        <div style={{ display: 'flex', gap: '12px' }}>
          {isOwnProfile ? (
            <>
              {/* REVIEW: theme tokens */}
              <button
                onClick={() => navigate('/showcase')}
                style={{
                  background: colors.lightPurple,
                  color: '#000',
                  border: `1px solid ${colors.lightPurple}`,
                  borderRadius: '6px',
                  padding: '4px 14px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Rethink Sans', sans-serif",
                }}
              >
                Edit Page
              </button>
              {/* REVIEW: theme tokens */}
              <button
                onClick={() => navigate('/feed')}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  border: `1px solid ${colors.textSecondary}`,
                  borderRadius: '6px',
                  padding: '4px 14px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Rethink Sans', sans-serif",
                }}
              >
                Back to App
              </button>
            </>
          ) : (
            <>
              {/* REVIEW: theme tokens */}
              <button
                onClick={() => navigate(`/profile/${targetUserId}`)}
                style={{
                  background: 'transparent',
                  color: colors.lightPurple,
                  border: `1px solid ${colors.lightPurple}`,
                  borderRadius: '6px',
                  padding: '4px 14px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Rethink Sans', sans-serif",
                }}
              >
                View Profile
              </button>
              {/* REVIEW: theme tokens */}
              <button
                onClick={() => navigate('/feed')}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  border: `1px solid ${colors.textSecondary}`,
                  borderRadius: '6px',
                  padding: '4px 14px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Rethink Sans', sans-serif",
                }}
              >
                Back to App
              </button>
            </>
          )}
        </div>
      </div>
    ) : null;

  // Render themed templates (bold, clean, creative, pop)
  if (theme === 'bold' || theme === 'clean' || theme === 'creative' || theme === 'pop') {
    return (
      <>
        {inAppBanner}
        {qrButton}
        {qrModal}
        <TemplateRenderer
          theme={theme}
          liveProfileData={liveProfileData}
          basicUser={basicUser}
          isOwnProfile={canEdit}
          onEdit={() => navigate('/profile-settings')}
          onTextClick={handleTextClick}
          onColorClick={handleColorClick}
          onPhotoClick={handlePhotoClick}
          galleryEditMode={galleryEditMode}
          onToggleGalleryEdit={handleGalleryEdit}
          onGalleryConfigChange={(newConfig) =>
            handleDesignChange('gallery_grid_config', newConfig)
          }
          onGalleryReorder={handleGalleryReorder}
          galleryConfig={editableSettings?.gallery_grid_config}
          onGalleryPhotoCrop={handleGalleryPhotoCrop}
          hiddenSections={hiddenSections}
          onToggleSection={handleToggleSection}
          professions={professions}
        />
        {designPanelElement}
        {inlinePopoverElement}
        {colorPopoverElement}
        {photoPickerElement}
        {galleryCropElement}
      </>
    );
  }

  // Legacy/custom template fallback
  return (
    <>
      {inAppBanner}
      {qrButton}
      {qrModal}
      <LegacyTemplate
        settings={liveProfileData.settings}
        performer={liveProfileData.performer}
        credits={liveProfileData.credits}
        skills={liveProfileData.skills}
        media={liveProfileData.media}
        links={liveProfileData.links}
        basicUser={basicUser}
        isOwnProfile={canEdit}
        onNavigateEdit={() => navigate('/profile-settings')}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        activeCreditsTab={activeCreditsTab}
        setActiveCreditsTab={setActiveCreditsTab}
        hasApplauded={hasApplauded}
        applauseCount={applauseCount}
        onApplause={handleApplause}
        designPanel={designPanelElement}
        inlinePopover={inlinePopoverElement}
      />
    </>
  );
}

export default Showcase;
